// src/components/CareerMap.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactFlow, { Background } from 'reactflow';
import MapControls from './MapControls.jsx';
import 'reactflow/dist/style.css'; 
import debounce from 'lodash/debounce';

import { generarNodosParaVista, generarEdgesParaNodosVisibles, getLayoutWithELK } from '../utils/graphUtils.js';
import { obtenerPrerrequisitosRecursivos } from '../utils/careerLogic.js';
import { getDirectUnlocks } from '../utils/careerLogicExtra.js';
import styles from '../styles/CareerMap.module.css';
import CorrelativasPanel from './CorrelativasPanel.jsx';
import MapLegend from './MapLegend.jsx';
import SubjectNode from './SubjectNode.js'; 
import CBCGroup from './CBCGroup';
import CBCMaster from './CBCMaster';

const CareerMap = ({
  plan,
  materias, 
  onMateriaChangeStatus,
  onNotaChange,
  orientacionSeleccionada,
  onSelectOrientacion,
  listaOrientaciones,
  cpcAprobadasCount,
  cpoPuntos,
  mostrarCPOdetallado,
  toggleMostrarCPOdetallado,
  materiaSeleccionadaParaExplorar,
  onExplorarMateria,
  prerrequisitosExplorar,
  habilitadasExplorar,
  onPuntosChange,
  hasConfirmedRequirements,
  onConfirmRequirements,
  onAprobarTodasCBC,
  cbcExpanded,
  onToggleCbcExpanded,
  cbcAprobado,
  recentlyChangedIds
}) => {

  // Use useRef to create a stable reference to nodeTypes
  const nodeTypesRef = useRef({
    subject: SubjectNode, // Mapea correctamente type: "subject"
    cbcGroup: CBCGroup,
    cbcMaster: CBCMaster
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showConfirmDialog, setShowConfirmDialog] = useState(!hasConfirmedRequirements);
  const [elements, setElements] = useState({ nodes: [], edges: [] });
  const [isLayouting, setIsLayouting] = useState(false);
  const [rfInstance, setRfInstance] = useState(null);
  const didInitialFitRef = useRef(false);

  const containerRef = useRef(null);

  const handleConfirmRequirements = () => {
    onConfirmRequirements();
    setShowConfirmDialog(false);
    toggleMostrarCPOdetallado();
  };

  const reqUnlockSets = useMemo(() => {
    if (!materiaSeleccionadaParaExplorar) return { reqSet: new Set(), unlockSet: new Set() };
    const reqSet = obtenerPrerrequisitosRecursivos(materiaSeleccionadaParaExplorar, plan, materias);
    const unlockDirect = getDirectUnlocks(materiaSeleccionadaParaExplorar, plan);
    const unlockSet = new Set(unlockDirect.map((x) => x.id));
    return { reqSet, unlockSet };
  }, [materiaSeleccionadaParaExplorar, plan, materias]);

  const updateLayout = useCallback(() => {
    if (!plan || !plan.grupos || !materias || isLayouting) {
      return;
    }

    setIsLayouting(true);

    try {
      const nodos = generarNodosParaVista(plan, materias, {
        showCBC: false,
        showCPO: true,
        materiaSeleccionadaParaExplorar,
        prerrequisitosExplorar: reqUnlockSets.reqSet,
        habilitadasExplorar: reqUnlockSets.unlockSet,
        orientacionSeleccionada,
        onMateriaChangeStatus,
        onNotaChange,
        onExplorarMateria,
        onAprobarTodasCBC,
        cbcExpanded,
        onToggleCbcExpanded,
        recentlyChangedIds,
        cbcAprobadoFlag: cbcAprobado === true
      });

      const conexiones = generarEdgesParaNodosVisibles(nodos, materias, {
        selectedId: materiaSeleccionadaParaExplorar,
        reqSet: reqUnlockSets.reqSet,
        unlockSet: reqUnlockSets.unlockSet,
      });
      // Layout ortogonal con ELK
      getLayoutWithELK(nodos, conexiones).then((layouted) => {
        setElements({ nodes: layouted.nodes, edges: layouted.edges });
      });
    } catch (error) {
      console.error('Error updating layout:', error);
    } finally {
      setIsLayouting(false);
    }
  }, [plan, materias, materiaSeleccionadaParaExplorar, orientacionSeleccionada, onMateriaChangeStatus, onNotaChange, onExplorarMateria, onAprobarTodasCBC, isLayouting, cbcExpanded, onToggleCbcExpanded, cbcAprobado, recentlyChangedIds, reqUnlockSets]);

  useEffect(() => {
    const debouncedResize = debounce(() => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
    }, 250);

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      debouncedResize.cancel();
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateLayout();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [updateLayout]);

  // Force fitView when elements change
  useEffect(() => {
    if (elements.nodes && elements.nodes.length > 0) {
      const timeoutId = setTimeout(() => {
        // Try to force ReactFlow to show the nodes
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [elements.nodes]);

  // Fit inicial una sola vez para evitar zoom out constante
  useEffect(() => {
    if (rfInstance && elements.nodes && elements.nodes.length > 0 && !didInitialFitRef.current) {
      didInitialFitRef.current = true;
      setTimeout(() => {
        rfInstance.fitView({ padding: 0.2, duration: 600 });
      }, 100);
    }
  }, [elements.nodes, rfInstance]);

  // Al cambiar la selección para explorar, centrar una sola vez (no recenter en cada actualización de nodos)
  useEffect(() => {
    if (!rfInstance || !materiaSeleccionadaParaExplorar || !elements.nodes) return;
    const node = elements.nodes.find((n) => n.id === materiaSeleccionadaParaExplorar);
    if (!node) return;
    const fallbackWidth = 240;
    const fallbackHeight = 100;
    const width = node.width || fallbackWidth;
    const height = node.height || fallbackHeight;
    const centerX = node.position.x + width / 2;
    const centerY = node.position.y + height / 2;
    const desiredZoom = 1.25;
    try {
      rfInstance.setCenter(centerX, centerY, { zoom: desiredZoom, duration: 600 });
    } catch (e) {
      // no-op
    }
    // Nota: no dependemos de elements.nodes para evitar re-centrar en cada cambio de estado
  }, [materiaSeleccionadaParaExplorar, rfInstance, elements.nodes]);

  // Verificar dimensiones del contenedor (debug)
  useEffect(() => {
    if (containerRef.current) {
      // Container dimensions available if needed
      containerRef.current.getBoundingClientRect();
    }
  }, []);

  if (!plan || !materias) {
    console.error('CareerMap: Required props missing');
    return <div>Error: Faltan datos requeridos</div>;
  }

  // Debug logs (comentados para producción)
  // console.log('CareerMap: Rendering with elements - nodes:', elements.nodes.length, 'edges:', elements.edges.length);

  // Si no hay elementos, mostrar un mensaje de carga
  if (!elements.nodes || elements.nodes.length === 0) {
    return (
      <div className="career-container" style={{ width: '100%', height: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '3em', marginBottom: '20px' }}>🔄</div>
          <h3>Cargando mapa de materias...</h3>
          <p>Generando nodos y conexiones...</p>
        </div>
      </div>
    );
  }

  // Debug logs (comentados para producción)
  // console.log('CareerMap: About to render ReactFlow with:', { nodesCount: elements.nodes.length });

  return (
    <div 
      className={styles.careerMapWrapper}
      ref={containerRef}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Banner de gating por CBC */}
      {!cbcAprobado && (<div className={styles.cbcBanner}>
        <span>Para habilitar materias del CPC necesitás completar el CBC.</span>
      </div>)}
      <ReactFlow
        nodes={elements.nodes}
        edges={elements.edges}
        nodeTypes={nodeTypesRef.current}
        onInit={setRfInstance}
        fitView={false}
        fitViewOptions={{ padding: 0.2, includeHiddenNodes: false }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        minZoom={0.3}
        maxZoom={2.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnDrag={true}
        zoomOnScroll={false}
        zoomOnPinch={true}
        panOnScroll={true}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        deleteKeyCode={null}
        multiSelectionKeyCode={null}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: 'step', animated: false }}
        onPaneClick={() => onExplorarMateria && onExplorarMateria(null)}
        className={styles.reactFlowContainer}
        style={{ touchAction: 'none' }}
      >
        <Background />
        <MapControls
          onZoomIn={() => rfInstance && rfInstance.zoomIn?.({ duration: 150 })}
          onZoomOut={() => rfInstance && rfInstance.zoomOut?.({ duration: 150 })}
          onFit={() => rfInstance && rfInstance.fitView?.({ padding: 0.2, includeHiddenNodes: false })}
          canZoomIn={true}
          canZoomOut={true}
        />
      </ReactFlow>
      {/* Botón CBC aprobado */}
      {typeof onAprobarTodasCBC === 'function' && (
        <div style={{ position: 'absolute', left: 8, top: 44, zIndex: 12 }}>
          <button
            className={styles.smallButton}
            onClick={() => onAprobarTodasCBC()}
            disabled={cbcAprobado === true}
            title="Marcar CBC aprobado"
          >
            {cbcAprobado ? 'CBC aprobado ✅' : 'CBC aprobado'}
          </button>
        </div>
      )}
      <MapLegend />
      <CorrelativasPanel
        selectedId={materiaSeleccionadaParaExplorar}
        plan={plan}
        materias={materias}
        onClose={() => onExplorarMateria && onExplorarMateria(null)}
      />
      {/* Botón reset/centrar mapa */}
      {rfInstance && (
        <div className={styles.topRightButtons}>
          <button
            onClick={() => rfInstance.fitView({ padding: 0.25, duration: 600 })}
            className={styles.smallButton}
            title="Centrar mapa"
          >
            Centrar mapa
          </button>
        </div>
      )}
      {showConfirmDialog && (
        <div 
          className="welcome-modal-overlay"
          style={{
            position: 'fixed',
            zIndex: 9999,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '15px' : '20px',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            className="welcome-modal-content"
            style={{
              backgroundColor: 'var(--bg-panel)',
              padding: isMobile ? '25px 20px' : '30px',
              borderRadius: isMobile ? '16px' : '12px',
              maxWidth: isMobile ? '90vw' : '500px',
              maxHeight: isMobile ? '85vh' : 'auto',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
              color: 'var(--text-primary)',
              overflow: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: isMobile ? '300px' : 'auto'
            }}
          >
            <h3 style={{ 
              marginBottom: '20px', 
              color: 'var(--accent-color)',
              fontSize: isMobile ? '1.4em' : '1.5em',
              fontWeight: '600',
              lineHeight: '1.3'
            }}>
              ¡Bienvenido al Mapa de Abogacía UBA!
            </h3>
            <p style={{ 
              marginBottom: '20px', 
              lineHeight: '1.6',
              fontSize: isMobile ? '0.95em' : '1em',
              textAlign: isMobile ? 'left' : 'center'
            }}>
              Este mapa te ayudará a planificar tu carrera. Podés hacer <strong>click</strong> en las materias para explorar correlatividades 
              y cambiar su estado.
            </p>
            <p style={{ 
              marginBottom: '25px', 
              fontSize: isMobile ? '0.85em' : '0.9em', 
              color: 'var(--text-secondary)',
              textAlign: isMobile ? 'left' : 'center'
            }}>
              💡 <strong>Tip:</strong> En dispositivos móviles, usá gestos para hacer zoom y mover el mapa.
            </p>
            <button
              onClick={handleConfirmRequirements}
              style={{
                padding: isMobile ? '12px 20px' : '12px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: 'var(--accent-color)',
                color: 'var(--text-inverse)',
                cursor: 'pointer',
                fontSize: isMobile ? '1em' : '1em',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                width: isMobile ? '100%' : 'auto',
                marginTop: 'auto'
              }}
            >
              Entendido, empezar 🚀
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CareerMap.propTypes = {
  plan: PropTypes.object.isRequired,
  materias: PropTypes.object.isRequired,
  onMateriaChangeStatus: PropTypes.func.isRequired,
  onNotaChange: PropTypes.func,
  orientacionSeleccionada: PropTypes.string,
  onSelectOrientacion: PropTypes.func,
  listaOrientaciones: PropTypes.array,
  cpcAprobadasCount: PropTypes.number,
  cpoPuntos: PropTypes.number,
  mostrarCPOdetallado: PropTypes.bool,
  toggleMostrarCPOdetallado: PropTypes.func,
  materiaSeleccionadaParaExplorar: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onExplorarMateria: PropTypes.func,
  prerrequisitosExplorar: PropTypes.object,
  habilitadasExplorar: PropTypes.object,
  onPuntosChange: PropTypes.func,
  hasConfirmedRequirements: PropTypes.bool,
  onConfirmRequirements: PropTypes.func,
  onAprobarTodasCBC: PropTypes.func,
  cbcExpanded: PropTypes.bool,
  onToggleCbcExpanded: PropTypes.func,
  cbcAprobado: PropTypes.bool,
  recentlyChangedIds: PropTypes.instanceOf(Set),
};

export default CareerMap;
